import { Modal, Box, Typography, IconButton, Fade, Backdrop, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ReactNode, useState } from 'react';

export interface CustomAction {
  label: string;
  onClick: () => Promise<void> | void;
  variant?: 'contained' | 'outlined';
  disabled?: boolean;
  loading?: boolean;
  icon?: ReactNode;
}

interface ItemsModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  showDownloadButton?: boolean;
  onDownload?: () => Promise<void>;
  showCustomActions?: boolean;
  customActions?: CustomAction[];
  isLoading?: boolean;
  isEvent?: boolean
  modalStyles?: object;
}

export const ItemsModal = ({
  open,
  onClose,
  title,
  children,
  showDownloadButton = false,
  onDownload,
  showCustomActions = false,
  customActions = [],
  isLoading = false,
  isEvent = false,
  modalStyles = {}
}: ItemsModalProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  // 1. Estilos base del contenedor del Modal (incluyendo el width predeterminado)
  const baseModalStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // Ancho predeterminado:
    width: isEvent ?
      { xs: '90%', sm: '65%', md: '50%', lg: '40%' } :
      { xs: '90%', sm: '85%', md: '80%', lg: '70%' },
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };
  const handleDownload = async () => {
    if (!onDownload) return;
    setIsDownloading(true);
    try {
      await onDownload();
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{...baseModalStyles, ...modalStyles}}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: { xs: 2, md: 3 },
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb'
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1F2937' }}>
              {title}
            </Typography>
            <IconButton onClick={onClose} sx={{ color: '#6b7280' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              overflowY: 'auto',
              p: { xs: 2, md: 3 },
              flexGrow: 1
            }}
          >
            {children}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              borderTop: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
              display: 'flex',
              justifyContent: (showDownloadButton && onDownload) || showCustomActions ? 'space-between' : 'flex-end',
              gap: 2,
              flexWrap: 'wrap'
            }}
          >
            {/* Download Button (backward compatibility) */}
            {showDownloadButton && onDownload && !showCustomActions && (
              <Button
                variant="contained"
                startIcon={isDownloading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <FileDownloadIcon />}
                onClick={handleDownload}
                disabled={isDownloading}
                sx={{
                  textTransform: 'none',
                  color: '#fff',
                  backgroundColor: 'primary.main',
                  transition: 'all 0.3s ease',
                  cursor: isDownloading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    transform: !isDownloading ? 'translateY(-3px)' : 'none',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)'
                  },
                  '&:disabled': {
                    backgroundColor: 'primary.light',
                    opacity: 0.7
                  }
                }}
              >
                {isDownloading ? 'Descargando...' : `Descargar`}
              </Button>
            )}

            {/* Custom Actions */}
            {showCustomActions && customActions.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                {customActions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || 'outlined'}
                    onClick={action.onClick}
                    disabled={action.disabled || isLoading || action.loading}
                    startIcon={action.loading ? <CircularProgress size={20} /> : action.icon}
                    sx={{
                      textTransform: 'none',
                      ...(action.variant === 'contained' && {
                        backgroundColor: 'primary.dark',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          opacity: 0.9,
                          transform: !action.disabled && !isLoading ? 'translateY(-3px)' : 'none'
                        }
                      }),
                      ...(action.variant === 'outlined' && {
                        color: '#6b7280',
                        border: '1px solid #e5e7eb',
                        '&:hover': {
                          backgroundColor: '#f3f4f6',
                          transform: !action.disabled && !isLoading ? 'translateY(-3px)' : 'none'
                        }
                      })
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Close Button (backward compatibility) */}
            {!showCustomActions && (
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  textTransform: 'none',
                  color: '#fff',
                  backgroundColor: 'secondary.main',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  border: 'none',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    opacity: 0.9,
                    transform: 'translateY(-3px)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)'
                  }
                }}
              >
                Cerrar
              </Button>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
